import { Button } from '@foam/buoy/src/components/Button/Button';
import { cn } from '@foam/buoy/src/lib/utils';
import { trackEvent } from '~/core/utils/trackEvent';
import { useTryNewFoam } from '~/hooks/useTryNewFoam';

import { SparkleIcon } from './SparkleIcon';

type TryNewFoamBannerProps = {
  className?: string;
};

/**
 * Opt-in banner for users still on classic. Purely presentational plus the CTA —
 * the automatic hand-off for users who have *already* opted in lives in
 * `useFluidInterfaceRedirect`, mounted app-wide from `_app`. Keeping it here
 * meant pages outside `LoggedLayout` never redirected (FOAM-22396).
 */
export const TryNewFoamBanner: React.FC<TryNewFoamBannerProps> = ({
  className,
}) => {
  const { isShowFluidToggle, isLoading, tryNewFoam } = useTryNewFoam();

  if (!isShowFluidToggle) return null;

  return (
    <section
      aria-label="Try the new Foam"
      className={cn(
        'relative isolate flex w-full min-h-[52px] items-center gap-3',
        'overflow-hidden border border-[rgba(251,188,65,0.22)] bg-[#fbbc41] px-8 py-1.5',
        className,
      )}
    >
      {/* Decorative blurred blobs from the design, layered over the flat orange. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(120%_700%_at_50%_0%,rgba(246,154,60,0.1)_0%,rgba(246,154,60,0)_70%),radial-gradient(60%_500%_at_8%_50%,rgba(246,154,60,0.06)_0%,rgba(246,154,60,0)_100%)]"
      />

      <div className="flex min-w-0 flex-1 items-center gap-2">
        <SparkleIcon className="size-4 text-[#1e2939]" />
        <p className="shrink-0 text-16 font-500 tracking-[0.2px] text-[#1e2939]">
          Meet the new foam
        </p>
        <p className="hidden truncate text-16 tracking-[0.2px] text-[#101828] md:block">
          A sharper look. A smarter way to discover.
        </p>
      </div>

      <div className="hidden shrink-0 items-center tab:flex">
        <span className="flex size-4 items-center justify-center">
          <span className="size-[6px] animate-pulse rounded-100 bg-[#032a0a]" />
        </span>
        <span className="text-[14px] leading-[18px] tracking-[0.2px] text-neutral-900">
          Now Live
        </span>
      </div>

      <Button
        variant="tertiary"
        loading={isLoading}
        iconEnd="ArrowRight"
        onClick={() => {
          trackEvent('foam-try-new-foam-banner-cta');
          tryNewFoam();
        }}
        track-id="foam-try-new-foam-banner-cta"
        className={cn(
          'h-10 shrink-0 gap-2 rounded-full border-[1.5px] border-neutral-900 px-6',
          'text-[16px] font-medium leading-5 tracking-[0.2px] text-[#101828]',
          // The gradient is a background-image, so it paints over whatever
          // background-color the variant contributes.
          'bg-[linear-gradient(140.13deg,#fbbc41_24.35%,#f69a3c_128.58%)]',
          'transition-opacity hover:opacity-90 active:opacity-80',
        )}
      >
        Try the new foam
      </Button>
    </section>
  );
};
